
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { getUserTeamIds } from "@/pages/guards/services/guardFetchService";

export const useTeams = () => {
  const { toast } = useToast();
  const { user } = useSupabaseUser();
  const { isAdmin, isSuperAdmin } = useUserRoles();

  return useQuery({
    queryKey: ['teams', user?.id, isAdmin, isSuperAdmin, 'v3'], // Updated version
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    placeholderData: undefined,
    queryFn: async () => {
      // If user is not logged in, return empty array
      if (!user) {
        console.log('No user found in useTeams, returning empty array');
        return [];
      }
      
      try {
        console.log('Fetching teams in useTeams hook. User ID:', user.id);
        
        // For admin users: fetch teams they have access to based on admin bubble
        if (isAdmin) {
          console.log(`Admin user - fetching accessible teams`);
          
          // Super admins see all teams
          if (isSuperAdmin) {
            console.log('Super admin - fetching all teams');
            const { data: teams, error } = await supabase
              .from('teams')
              .select('id, name, description, status, created_at, updated_at, created_by')
              .eq('status', 'active');

            if (error) {
              console.error('Error fetching teams in useTeams:', error);
              throw error;
            }
            
            console.log(`Teams fetched in useTeams for super admin (${teams?.length || 0} teams):`, teams);
            return teams || [];
          }
          
          // Regular admins and contributors - get admin bubble
          console.log('Regular admin/contributor - fetching admin group');
          const { data: adminGroup, error: groupError } = await supabase
            .rpc('get_admin_group', { user_id: user.id });

          if (groupError) {
            console.error('Error fetching admin group:', groupError);
            console.log('Falling back to current user only');
          }

          // Get all admin IDs in the bubble, or fallback to current user
          const adminIds = adminGroup?.map((g: any) => g.admin_id) || [user.id];
          console.log('Admin bubble IDs:', adminIds);

          // Fetch teams created by anyone in the admin bubble
          const { data: teams, error } = await supabase
            .from('teams')
            .select('id, name, description, status, created_at, updated_at, created_by')
            .in('created_by', adminIds)
            .eq('status', 'active');

          if (error) {
            console.error('Error fetching teams in useTeams:', error);
            throw error;
          }
          
          console.log(`Teams fetched in useTeams for admin (${teams?.length || 0} teams):`, teams);
          return teams || [];
        }
        
        // For non-admin users: fetch teams they have access to
        console.log(`Regular user - fetching assigned teams`);
        const userTeamIds = await getUserTeamIds(user.id);
        console.log('User team IDs in useTeams:', userTeamIds);
        
        if (userTeamIds.length === 0) {
          console.log('No teams found for this user in useTeams');
          return [];
        }
        
        const { data: teams, error } = await supabase
          .from('teams')
          .select('id, name, description, status, created_at, updated_at')
          .in('id', userTeamIds)
          .eq('status', 'active');

        if (error) {
          console.error('Error fetching teams in useTeams:', error);
          throw error;
        }
        
        console.log(`Teams fetched in useTeams (${teams?.length || 0} teams):`, teams);
        return teams || [];
      } catch (error: any) {
        console.error('Error fetching teams in useTeams:', error);
        toast({
          title: "Error loading teams",
          description: error.message || "Could not load teams",
          variant: "destructive",
        });
        return [];
      }
    },
    enabled: !!user,
  });
};
